'use client';

import QueryBuilder from '../query-builder/QueryBuilder';
import { pluralizeLabel, validateQuery } from '../query-builder/query-utils';
import { getOperatorLabel } from '../query-builder/schema';
import type { QueryDefinition, QueryField, QueryRule, QueryValue } from '../query-builder/types';
import FilterButton from '../ui/FilterButton';
import Popover from '../ui/Popover';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSignalDateQueryField, createSignalTypeQueryField } from './signalQueryFieldPresets';
import useWebsiteSignalQueryFields from './useWebsiteSignalQueryFields';
import {
  buildWebsiteSignalFilterPayload,
  type WebsiteSignalFilterPayload,
} from './websiteSignalFilterPayload';
import { useTranslations } from 'next-intl';

import { SIGNAL_FILTER_CONFIGS } from './signalFilterConfigs';

export type SignalDetailsFilter = WebsiteSignalFilterPayload;
export type SignalDetailsFilterSummary = {
  id: string;
  field: string;
  fieldLabel: string;
  operatorLabel: string;
  valueLabel: string;
  valueLoading?: boolean;
  fieldDef?: QueryField;
  rule?: QueryRule;
};

const emptyQuery = (): QueryDefinition => ({
  groups: [
    {
      id: crypto.randomUUID(),
      join: 'AND',
      rules: [{ id: crypto.randomUUID(), join: 'AND', field: '', operator: '=', value: '' }],
    },
  ],
});

export default function SignalDetailsFilterPopover({
  clientId,
  onChange,
  onSummaryChange,
  onOpenChange,
  signalType = 'website',
}: {
  clientId: string;
  onChange: (filters: SignalDetailsFilter) => void;
  onSummaryChange?: (
    summaries: SignalDetailsFilterSummary[],
    remove: (ruleId: string) => void,
    updateValue: (ruleId: string, value: QueryValue) => void
  ) => void;
  onOpenChange?: (open: boolean) => void;
  signalType?: 'website' | 'market' | 'linkedin';
}) {
  const t = useTranslations('signals');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<QueryDefinition>(emptyQuery);
  const websiteFields = useWebsiteSignalQueryFields(clientId, {
    enabled: signalType === 'website',
  });
  const fields = useMemo<QueryField[]>(() => {
    const config = SIGNAL_FILTER_CONFIGS[signalType];
    const fieldsForSignalType: QueryField[] = [
      ...(signalType === 'website' ? [createSignalTypeQueryField()] : []),
      {
        name: 'action',
        label: t('filters.action'),
        type: 'select',
        operators: ['=', 'in', 'notIn'],
        options: (signalType === 'market' || signalType === 'linkedin'
          ? config.triggers
          : ['visited_page']
        ).map(value => ({
          value,
          label: t(`actions.${value}` as never),
        })),
        defaultValue: '',
      },
      createSignalDateQueryField(),
      ...(signalType === 'website' ? websiteFields.filter(field => field.name === 'url') : []),
    ];

    return fieldsForSignalType;
  }, [signalType, t, websiteFields]);
  const lastValue = useRef('[]');
  const lastSummaryValue = useRef('[]');
  const validRules = useMemo(
    () =>
      query.groups.flatMap(group =>
        group.rules.filter(
          rule => validateQuery({ groups: [{ ...group, rules: [rule] }] }, fields).length === 0
        )
      ),
    [fields, query]
  );
  const filterPayload = useMemo<SignalDetailsFilter>(
    () =>
      validRules.length
        ? buildWebsiteSignalFilterPayload({
            groups: query.groups
              .map(group => ({
                ...group,
                rules: group.rules.filter(rule => validRules.some(valid => valid.id === rule.id)),
              }))
              .filter(group => group.rules.length > 0),
          })
        : { match: 'all', filters: [] },
    [query.groups, validRules]
  );
  const removeRule = useCallback(
    (ruleId: string) => {
      const remainingRules = query.groups
        .flatMap(group => group.rules)
        .filter(rule => rule.id !== ruleId);
      setQuery(
        remainingRules.length
          ? {
              groups: query.groups
                .map(group => ({
                  ...group,
                  rules: group.rules.filter(rule => rule.id !== ruleId),
                }))
                .filter(group => group.rules.length),
            }
          : emptyQuery()
      );
    },
    [query]
  );
  const updateRuleValue = useCallback(
    (ruleId: string, value: QueryValue) => {
      setQuery({
        groups: query.groups.map(group => ({
          ...group,
          rules: group.rules.map(rule => (rule.id === ruleId ? { ...rule, value } : rule)),
        })),
      });
    },
    [query]
  );

  useEffect(() => {
    const serialized = JSON.stringify(filterPayload);
    if (serialized === lastValue.current) return;
    lastValue.current = serialized;
    onChange(filterPayload);
  }, [filterPayload, onChange]);

  useEffect(() => {
    const summaries = validRules.map(rule => {
      const field = fields.find(candidate => candidate.name === rule.field)!;
      const values = Array.isArray(rule.value) ? rule.value : [rule.value];
      const resolvedOptions = values.map(value =>
        field.options?.find(candidate => candidate.value === String(value))
      );
      const valueLabels = values.map(
        (value, index) => resolvedOptions[index]?.label || String(value)
      );
      const valueLoading = Boolean(
        field.type === 'select' &&
        field.searchLoading &&
        values.some((value, index) => value != null && value !== '' && !resolvedOptions[index])
      );
      return {
        id: rule.id,
        field: rule.field,
        fieldLabel: field.label,
        operatorLabel: getOperatorLabel(rule.operator, field.type),
        valueLabel:
          valueLabels.length > 1
            ? `${valueLabels.length} ${pluralizeLabel(field.label)}`
            : valueLabels[0] || '',
        valueLoading,
        fieldDef: field,
        rule,
      };
    });
    const serialized = JSON.stringify(summaries);
    if (serialized === lastSummaryValue.current) return;

    lastSummaryValue.current = serialized;
    onSummaryChange?.(summaries, removeRule, updateRuleValue);
  }, [fields, onSummaryChange, removeRule, updateRuleValue, validRules]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen && validRules.length === 0) {
      setQuery(emptyQuery());
    }
  };

  return (
    <Popover
      popoverProps={{ open, onOpenChange: handleOpenChange, modal: false }}
      contentClassName="w-[min(720px,calc(100vw-32px))] p-0"
      trigger={<FilterButton count={validRules.length} />}
    >
      <div className="border-b px-4 py-3 text-base font-semibold">Filter</div>
      <div className="px-4 py-4">
        <QueryBuilder compact fields={fields} value={query} onChange={setQuery} />
      </div>
    </Popover>
  );
}
